// 100 most common PT injuries
// viewBox format: "x y width height" — zooms SVG into body region (default full: "0 0 200 460")
// layer: muscle | bone | ligament | tendon | nerve | joint | disc | bursa

export const LAYER_META = {
  muscle:   { color: '#ef4444', light: '#fef2f2', border: '#fecaca', label: 'Muscle' },
  bone:     { color: '#94a3b8', light: '#f8fafc', border: '#e2e8f0', label: 'Bone' },
  ligament: { color: '#f97316', light: '#fff7ed', border: '#fed7aa', label: 'Ligament' },
  tendon:   { color: '#eab308', light: '#fefce8', border: '#fde68a', label: 'Tendon' },
  nerve:    { color: '#a855f7', light: '#faf5ff', border: '#e9d5ff', label: 'Nerve' },
  joint:    { color: '#06b6d4', light: '#ecfeff', border: '#a5f3fc', label: 'Joint' },
  disc:     { color: '#ec4899', light: '#fdf2f8', border: '#fbcfe8', label: 'Disc' },
  bursa:    { color: '#10b981', light: '#ecfdf5', border: '#a7f3d0', label: 'Bursa' },
}

// ViewBox zoom presets
const VB = {
  full:           '0 0 200 460',
  head:           '62 5 76 90',
  neck:           '70 55 60 55',
  upperBack:      '50 80 100 65',
  midBack:        '52 128 96 58',
  lowerBack:      '52 162 96 60',
  bothShoulders:  '10 80 180 70',
  leftShoulder:   '10 80 95 68',
  rightShoulder:  '95 80 95 68',
  chest:          '52 80 96 75',
  abdomen:        '52 148 96 72',
  leftElbow:      '8 158 65 55',
  rightElbow:     '127 158 65 55',
  leftWrist:      '5 218 60 62',
  rightWrist:     '135 218 60 62',
  bothHips:       '38 208 124 55',
  leftHip:        '44 208 80 52',
  rightHip:       '76 208 80 52',
  leftKnee:       '44 305 74 70',
  rightKnee:      '82 305 74 70',
  leftAnkle:      '40 392 70 50',
  rightAnkle:     '88 392 70 50',
}

export const INJURIES = [
  //  KNEE 
  { id: 'acl_tear', name: 'ACL Tear', fullName: 'Anterior Cruciate Ligament (ACL) Tear',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'ligament', tags: ['knee', 'ligament', 'sports'], category: 'Knee' },

  { id: 'mcl_tear', name: 'MCL Tear', fullName: 'Medial Collateral Ligament (MCL) Tear',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'ligament', tags: ['knee', 'ligament', 'contact'], category: 'Knee' },

  { id: 'lcl_tear', name: 'LCL Tear', fullName: 'Lateral Collateral Ligament (LCL) Tear',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'side_left', viewBox: VB.leftKnee,
    layer: 'ligament', tags: ['knee', 'ligament', 'lateral'], category: 'Knee' },

  { id: 'pcl_tear', name: 'PCL Tear', fullName: 'Posterior Cruciate Ligament (PCL) Tear',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'ligament', tags: ['knee', 'ligament', 'trauma'], category: 'Knee' },

  { id: 'meniscus_tear', name: 'Meniscus Tear', fullName: 'Meniscus Tear (Medial or Lateral)',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'joint', tags: ['knee', 'cartilage', 'sports'], category: 'Knee' },

  { id: 'runners_knee', name: "Runner's Knee", fullName: 'Patellofemoral Pain Syndrome',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'joint', tags: ['knee', 'overuse', 'running'], category: 'Knee' },

  { id: 'patellar_tendinitis', name: "Jumper's Knee", fullName: 'Patellar Tendinitis / Tendinopathy',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'tendon', tags: ['knee', 'tendon', 'sports'], category: 'Knee' },

  { id: 'it_band_syndrome', name: 'IT Band Syndrome', fullName: 'Iliotibial Band Syndrome',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'side_left', viewBox: VB.leftKnee,
    layer: 'tendon', tags: ['knee', 'hip', 'running', 'overuse', 'lateral'], category: 'Knee' },

  { id: 'knee_bursitis', name: 'Knee Bursitis', fullName: 'Prepatellar / Pes Anserine Bursitis',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'bursa', tags: ['knee', 'bursa', 'inflammation'], category: 'Knee' },

  { id: 'chondromalacia', name: 'Chondromalacia Patella', fullName: 'Chondromalacia Patella (Cartilage Softening)',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'bone', tags: ['knee', 'cartilage'], category: 'Knee' },

  { id: 'knee_oa', name: 'Knee Osteoarthritis', fullName: 'Knee Osteoarthritis (Degenerative Joint Disease)',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'joint', tags: ['knee', 'arthritis', 'degenerative'], category: 'Knee' },

  { id: 'quad_tendon_rupture', name: 'Quad Tendon Rupture', fullName: 'Quadriceps Tendon Rupture',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'tendon', tags: ['knee', 'tendon', 'trauma'], category: 'Knee' },

  { id: 'osgood_schlatter', name: 'Osgood-Schlatter', fullName: "Osgood-Schlatter Disease",
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'bone', tags: ['knee', 'adolescent', 'growth'], category: 'Knee' },

  { id: 'post_acl', name: 'Post-ACL Surgery', fullName: 'Post-ACL Reconstruction Rehabilitation',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'ligament', tags: ['knee', 'surgical', 'recovery'], category: 'Knee' },

  { id: 'post_knee_replacement', name: 'Post-Knee Replacement', fullName: 'Total Knee Arthroplasty Rehabilitation',
    bodyRegions: ['Left Knee', 'Right Knee'], view: 'front', viewBox: VB.leftKnee,
    layer: 'joint', tags: ['knee', 'surgical', 'arthroplasty'], category: 'Knee' },

  //  SHOULDER 
  { id: 'rotator_cuff_tear', name: 'Rotator Cuff Tear', fullName: 'Rotator Cuff Muscle / Tendon Tear',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'muscle', tags: ['shoulder', 'rotator cuff', 'muscle'], category: 'Shoulder' },

  { id: 'shoulder_impingement', name: 'Shoulder Impingement', fullName: 'Subacromial Shoulder Impingement Syndrome',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'bursa', tags: ['shoulder', 'impingement', 'overuse'], category: 'Shoulder' },

  { id: 'frozen_shoulder', name: 'Frozen Shoulder', fullName: 'Adhesive Capsulitis (Frozen Shoulder)',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'joint', tags: ['shoulder', 'stiffness', 'capsule'], category: 'Shoulder' },

  { id: 'slap_tear', name: 'SLAP Tear', fullName: 'Superior Labrum Anterior to Posterior (SLAP) Tear',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'ligament', tags: ['shoulder', 'labrum', 'overhead'], category: 'Shoulder' },

  { id: 'shoulder_dislocation', name: 'Shoulder Dislocation', fullName: 'Glenohumeral Joint Dislocation',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'joint', tags: ['shoulder', 'dislocation', 'trauma'], category: 'Shoulder' },

  { id: 'ac_separation', name: 'AC Joint Separation', fullName: 'Acromioclavicular (AC) Joint Separation',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'joint', tags: ['shoulder', 'ac joint', 'trauma'], category: 'Shoulder' },

  { id: 'shoulder_bursitis', name: 'Shoulder Bursitis', fullName: 'Subacromial Bursitis',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'bursa', tags: ['shoulder', 'bursa', 'inflammation'], category: 'Shoulder' },

  { id: 'biceps_tendinitis', name: 'Biceps Tendinitis', fullName: 'Proximal Biceps Tendinitis',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'tendon', tags: ['shoulder', 'biceps', 'tendon'], category: 'Shoulder' },

  { id: 'shoulder_instability', name: 'Shoulder Instability', fullName: 'Multidirectional Shoulder Instability',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'joint', tags: ['shoulder', 'instability', 'laxity'], category: 'Shoulder' },

  { id: 'post_shoulder_replacement', name: 'Post-Shoulder Replacement', fullName: 'Total Shoulder Arthroplasty Rehabilitation',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'joint', tags: ['shoulder', 'surgical', 'arthroplasty'], category: 'Shoulder' },

  { id: 'thoracic_outlet', name: 'Thoracic Outlet Syndrome', fullName: 'Thoracic Outlet Syndrome (TOS)',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.bothShoulders,
    layer: 'nerve', tags: ['shoulder', 'neck', 'nerve', 'compression'], category: 'Shoulder' },

  { id: 'calcific_tendinitis', name: 'Calcific Tendinitis', fullName: 'Calcific Tendinitis of the Shoulder',
    bodyRegions: ['Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.leftShoulder,
    layer: 'tendon', tags: ['shoulder', 'calcium', 'rotator cuff'], category: 'Shoulder' },

  //  LOWER BACK 
  { id: 'lumbar_herniated_disc', name: 'Herniated Disc (Lumbar)', fullName: 'Lumbar Herniated / Bulging Disc',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'disc', tags: ['back', 'disc', 'herniation'], category: 'Lower Back' },

  { id: 'lumbar_stenosis', name: 'Lumbar Stenosis', fullName: 'Lumbar Spinal Stenosis',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'nerve', tags: ['back', 'stenosis', 'nerve'], category: 'Lower Back' },

  { id: 'si_joint', name: 'SI Joint Dysfunction', fullName: 'Sacroiliac (SI) Joint Dysfunction',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'joint', tags: ['back', 'si joint', 'pelvis'], category: 'Lower Back' },

  { id: 'lumbar_strain', name: 'Lower Back Strain', fullName: 'Lumbar Muscle / Ligament Strain',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'muscle', tags: ['back', 'muscle', 'strain'], category: 'Lower Back' },

  { id: 'spondylolisthesis', name: 'Spondylolisthesis', fullName: 'Lumbar Spondylolisthesis',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'bone', tags: ['back', 'vertebra', 'slip'], category: 'Lower Back' },

  { id: 'ddd_lumbar', name: 'Degenerative Disc Disease', fullName: 'Lumbar Degenerative Disc Disease',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'disc', tags: ['back', 'disc', 'degenerative', 'aging'], category: 'Lower Back' },

  { id: 'sciatica', name: 'Sciatica', fullName: 'Sciatica (Sciatic Nerve Irritation)',
    bodyRegions: ['Lower Back', 'Left Hip', 'Right Hip'], view: 'back', viewBox: VB.lowerBack,
    layer: 'nerve', tags: ['back', 'nerve', 'leg pain', 'sciatic'], category: 'Lower Back' },

  { id: 'piriformis_syndrome', name: 'Piriformis Syndrome', fullName: 'Piriformis Syndrome',
    bodyRegions: ['Lower Back', 'Left Hip', 'Right Hip'], view: 'back', viewBox: VB.bothHips,
    layer: 'muscle', tags: ['hip', 'back', 'nerve', 'piriformis'], category: 'Lower Back' },

  { id: 'lumbar_radiculopathy', name: 'Lumbar Radiculopathy', fullName: 'Lumbar Radiculopathy (Nerve Root Compression)',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'nerve', tags: ['back', 'nerve', 'radiculopathy'], category: 'Lower Back' },

  { id: 'post_spinal_fusion', name: 'Post-Spinal Fusion', fullName: 'Post-Spinal Fusion Rehabilitation',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'bone', tags: ['back', 'surgical', 'fusion'], category: 'Lower Back' },

  { id: 'facet_syndrome', name: 'Facet Joint Syndrome', fullName: 'Lumbar Facet Joint Syndrome',
    bodyRegions: ['Lower Back'], view: 'back', viewBox: VB.lowerBack,
    layer: 'joint', tags: ['back', 'facet', 'joint'], category: 'Lower Back' },

  { id: 'scoliosis', name: 'Scoliosis', fullName: 'Scoliosis (Spinal Curvature)',
    bodyRegions: ['Upper Back', 'Mid Back', 'Lower Back'], view: 'back', viewBox: VB.midBack,
    layer: 'bone', tags: ['back', 'spine', 'curvature'], category: 'Lower Back' },

  //  NECK / CERVICAL 
  { id: 'cervical_herniated_disc', name: 'Cervical Herniated Disc', fullName: 'Cervical Herniated / Bulging Disc',
    bodyRegions: ['Head / Neck'], view: 'back', viewBox: VB.neck,
    layer: 'disc', tags: ['neck', 'disc', 'herniation'], category: 'Neck' },

  { id: 'cervical_stenosis', name: 'Cervical Stenosis', fullName: 'Cervical Spinal Stenosis',
    bodyRegions: ['Head / Neck'], view: 'back', viewBox: VB.neck,
    layer: 'nerve', tags: ['neck', 'stenosis', 'nerve'], category: 'Neck' },

  { id: 'whiplash', name: 'Whiplash', fullName: 'Whiplash / Cervical Acceleration-Deceleration Injury',
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.neck,
    layer: 'muscle', tags: ['neck', 'whiplash', 'mva', 'trauma'], category: 'Neck' },

  { id: 'cervical_strain', name: 'Neck Strain', fullName: 'Cervical Muscle Strain',
    bodyRegions: ['Head / Neck'], view: 'back', viewBox: VB.neck,
    layer: 'muscle', tags: ['neck', 'muscle', 'strain'], category: 'Neck' },

  { id: 'cervical_radiculopathy', name: 'Cervical Radiculopathy', fullName: 'Cervical Radiculopathy (Pinched Nerve)',
    bodyRegions: ['Head / Neck'], view: 'back', viewBox: VB.neck,
    layer: 'nerve', tags: ['neck', 'nerve', 'arm pain', 'radiculopathy'], category: 'Neck' },

  { id: 'torticollis', name: 'Torticollis', fullName: 'Torticollis (Wry Neck)',
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.neck,
    layer: 'muscle', tags: ['neck', 'muscle', 'spasm'], category: 'Neck' },

  { id: 'text_neck', name: 'Text Neck', fullName: 'Text Neck Syndrome / Forward Head Posture',
    bodyRegions: ['Head / Neck', 'Upper Back'], view: 'back', viewBox: VB.neck,
    layer: 'muscle', tags: ['neck', 'posture', 'tech', 'overuse'], category: 'Neck' },

  //  HIP 
  { id: 'hip_labrum_tear', name: 'Hip Labrum Tear', fullName: 'Acetabular Labrum Tear',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'front', viewBox: VB.leftHip,
    layer: 'joint', tags: ['hip', 'labrum', 'cartilage'], category: 'Hip' },

  { id: 'fai', name: 'Hip Impingement (FAI)', fullName: 'Femoroacetabular Impingement (FAI)',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'front', viewBox: VB.leftHip,
    layer: 'bone', tags: ['hip', 'impingement', 'bone'], category: 'Hip' },

  { id: 'hip_bursitis', name: 'Hip Bursitis', fullName: 'Greater Trochanteric Bursitis',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'side_left', viewBox: VB.leftHip,
    layer: 'bursa', tags: ['hip', 'bursa', 'lateral hip pain'], category: 'Hip' },

  { id: 'hip_flexor_strain', name: 'Hip Flexor Strain', fullName: 'Hip Flexor Muscle Strain (Iliopsoas)',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'front', viewBox: VB.leftHip,
    layer: 'muscle', tags: ['hip', 'flexor', 'muscle'], category: 'Hip' },

  { id: 'gluteal_tendinopathy', name: 'Gluteal Tendinopathy', fullName: 'Gluteal Tendinopathy (Lateral Hip Pain)',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'back', viewBox: VB.bothHips,
    layer: 'tendon', tags: ['hip', 'gluteal', 'tendon', 'lateral'], category: 'Hip' },

  { id: 'hip_oa', name: 'Hip Osteoarthritis', fullName: 'Hip Osteoarthritis',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'front', viewBox: VB.bothHips,
    layer: 'joint', tags: ['hip', 'arthritis', 'degenerative'], category: 'Hip' },

  { id: 'post_hip_replacement', name: 'Post-Hip Replacement', fullName: 'Total Hip Arthroplasty Rehabilitation',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'front', viewBox: VB.leftHip,
    layer: 'joint', tags: ['hip', 'surgical', 'arthroplasty'], category: 'Hip' },

  { id: 'hip_pointer', name: 'Hip Pointer', fullName: 'Hip Pointer (Iliac Crest Contusion)',
    bodyRegions: ['Left Hip', 'Right Hip'], view: 'front', viewBox: VB.bothHips,
    layer: 'muscle', tags: ['hip', 'contusion', 'trauma', 'sports'], category: 'Hip' },

  //  ANKLE / FOOT 
  { id: 'ankle_sprain_mild', name: 'Ankle Sprain (Mild)', fullName: 'Grade 1 Lateral Ankle Sprain',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'side_left', viewBox: VB.leftAnkle,
    layer: 'ligament', tags: ['ankle', 'sprain', 'ligament', 'lateral'], category: 'Ankle & Foot' },

  { id: 'ankle_sprain_severe', name: 'Ankle Sprain (Severe)', fullName: 'Grade 2–3 Ankle Sprain / Ligament Tear',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'side_left', viewBox: VB.leftAnkle,
    layer: 'ligament', tags: ['ankle', 'sprain', 'ligament', 'severe', 'lateral'], category: 'Ankle & Foot' },

  { id: 'achilles_tendinitis', name: 'Achilles Tendinitis', fullName: 'Achilles Tendinitis / Tendinopathy',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'back', viewBox: VB.leftAnkle,
    layer: 'tendon', tags: ['ankle', 'achilles', 'tendon', 'running'], category: 'Ankle & Foot' },

  { id: 'achilles_rupture', name: 'Achilles Rupture', fullName: 'Achilles Tendon Rupture',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'back', viewBox: VB.leftAnkle,
    layer: 'tendon', tags: ['ankle', 'achilles', 'rupture', 'surgical'], category: 'Ankle & Foot' },

  { id: 'plantar_fasciitis', name: 'Plantar Fasciitis', fullName: 'Plantar Fasciitis',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'front', viewBox: VB.leftAnkle,
    layer: 'tendon', tags: ['foot', 'heel', 'plantar fascia'], category: 'Ankle & Foot' },

  { id: 'pttd', name: 'PTTD (Flat Foot)', fullName: 'Posterior Tibial Tendon Dysfunction',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'front', viewBox: VB.leftAnkle,
    layer: 'tendon', tags: ['ankle', 'foot', 'flat foot', 'tendon'], category: 'Ankle & Foot' },

  { id: 'peroneal_tendinitis', name: 'Peroneal Tendinitis', fullName: 'Peroneal Tendinitis / Tendinopathy',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'side_left', viewBox: VB.leftAnkle,
    layer: 'tendon', tags: ['ankle', 'lateral', 'tendon'], category: 'Ankle & Foot' },

  { id: 'ankle_impingement', name: 'Ankle Impingement', fullName: 'Anterior / Posterior Ankle Impingement',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'front', viewBox: VB.leftAnkle,
    layer: 'joint', tags: ['ankle', 'impingement', 'joint'], category: 'Ankle & Foot' },

  { id: 'stress_fracture_foot', name: 'Foot Stress Fracture', fullName: 'Metatarsal Stress Fracture',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'front', viewBox: VB.leftAnkle,
    layer: 'bone', tags: ['foot', 'fracture', 'overuse', 'running'], category: 'Ankle & Foot' },

  { id: 'mortons_neuroma', name: "Morton's Neuroma", fullName: "Morton's Neuroma",
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'front', viewBox: VB.leftAnkle,
    layer: 'nerve', tags: ['foot', 'nerve', 'toe pain'], category: 'Ankle & Foot' },

  //  ELBOW / WRIST / HAND 
  { id: 'tennis_elbow', name: "Tennis Elbow", fullName: 'Lateral Epicondylitis (Tennis Elbow)',
    bodyRegions: ['Left Elbow', 'Right Elbow'], view: 'front', viewBox: VB.leftElbow,
    layer: 'tendon', tags: ['elbow', 'lateral', 'overuse', 'tennis'], category: 'Elbow & Wrist' },

  { id: 'golfers_elbow', name: "Golfer's Elbow", fullName: 'Medial Epicondylitis (Golfer\'s Elbow)',
    bodyRegions: ['Left Elbow', 'Right Elbow'], view: 'front', viewBox: VB.leftElbow,
    layer: 'tendon', tags: ['elbow', 'medial', 'overuse', 'golf'], category: 'Elbow & Wrist' },

  { id: 'carpal_tunnel', name: 'Carpal Tunnel Syndrome', fullName: 'Carpal Tunnel Syndrome',
    bodyRegions: ['Left Wrist / Hand', 'Right Wrist / Hand'], view: 'front', viewBox: VB.leftWrist,
    layer: 'nerve', tags: ['wrist', 'hand', 'nerve', 'compression'], category: 'Elbow & Wrist' },

  { id: 'ulnar_nerve', name: 'Ulnar Nerve Entrapment', fullName: 'Ulnar Nerve Entrapment at Elbow',
    bodyRegions: ['Left Elbow', 'Right Elbow'], view: 'front', viewBox: VB.leftElbow,
    layer: 'nerve', tags: ['elbow', 'ulnar nerve', 'cubital tunnel'], category: 'Elbow & Wrist' },

  { id: 'wrist_sprain', name: 'Wrist Sprain', fullName: 'Wrist Ligament Sprain',
    bodyRegions: ['Left Wrist / Hand', 'Right Wrist / Hand'], view: 'front', viewBox: VB.leftWrist,
    layer: 'ligament', tags: ['wrist', 'sprain', 'ligament'], category: 'Elbow & Wrist' },

  { id: 'de_quervain', name: "De Quervain's", fullName: "De Quervain's Tenosynovitis",
    bodyRegions: ['Left Wrist / Hand', 'Right Wrist / Hand'], view: 'front', viewBox: VB.leftWrist,
    layer: 'tendon', tags: ['wrist', 'thumb', 'tendon', 'overuse'], category: 'Elbow & Wrist' },

  { id: 'trigger_finger', name: 'Trigger Finger', fullName: 'Trigger Finger (Stenosing Tenosynovitis)',
    bodyRegions: ['Left Wrist / Hand', 'Right Wrist / Hand'], view: 'front', viewBox: VB.leftWrist,
    layer: 'tendon', tags: ['finger', 'tendon', 'hand'], category: 'Elbow & Wrist' },

  { id: 'elbow_bursitis', name: 'Elbow Bursitis', fullName: 'Olecranon Bursitis',
    bodyRegions: ['Left Elbow', 'Right Elbow'], view: 'front', viewBox: VB.leftElbow,
    layer: 'bursa', tags: ['elbow', 'bursa', 'swelling'], category: 'Elbow & Wrist' },

  { id: 'cubital_tunnel', name: 'Cubital Tunnel Syndrome', fullName: 'Cubital Tunnel Syndrome',
    bodyRegions: ['Left Elbow', 'Right Elbow'], view: 'front', viewBox: VB.leftElbow,
    layer: 'nerve', tags: ['elbow', 'ulnar nerve', 'numbness'], category: 'Elbow & Wrist' },

  { id: 'dupuytren', name: "Dupuytren's Contracture", fullName: "Dupuytren's Contracture",
    bodyRegions: ['Left Wrist / Hand', 'Right Wrist / Hand'], view: 'front', viewBox: VB.leftWrist,
    layer: 'tendon', tags: ['hand', 'finger', 'contracture', 'fascia'], category: 'Elbow & Wrist' },

  //  UPPER / MID BACK 
  { id: 'thoracic_herniated_disc', name: 'Thoracic Herniated Disc', fullName: 'Thoracic Herniated Disc',
    bodyRegions: ['Mid Back'], view: 'back', viewBox: VB.midBack,
    layer: 'disc', tags: ['back', 'thoracic', 'disc'], category: 'Upper & Mid Back' },

  { id: 'postural_dysfunction', name: 'Postural Dysfunction', fullName: 'Postural Dysfunction / Upper Crossed Syndrome',
    bodyRegions: ['Upper Back', 'Head / Neck'], view: 'back', viewBox: VB.upperBack,
    layer: 'muscle', tags: ['posture', 'upper back', 'neck'], category: 'Upper & Mid Back' },

  { id: 'kyphosis', name: 'Thoracic Kyphosis', fullName: 'Thoracic Hyperkyphosis (Rounded Upper Back)',
    bodyRegions: ['Upper Back', 'Mid Back'], view: 'back', viewBox: VB.upperBack,
    layer: 'bone', tags: ['back', 'posture', 'kyphosis', 'spine'], category: 'Upper & Mid Back' },

  { id: 'rib_stress_fracture', name: 'Rib Stress Fracture', fullName: 'Rib Stress Fracture',
    bodyRegions: ['Chest / Ribs'], view: 'front', viewBox: VB.chest,
    layer: 'bone', tags: ['rib', 'fracture', 'overuse', 'rowing'], category: 'Upper & Mid Back' },

  { id: 'costochondritis', name: 'Costochondritis', fullName: 'Costochondritis (Chest Wall Pain)',
    bodyRegions: ['Chest / Ribs'], view: 'front', viewBox: VB.chest,
    layer: 'joint', tags: ['chest', 'rib', 'cartilage', 'inflammation'], category: 'Upper & Mid Back' },

  //  ABDOMEN / PELVIC 
  { id: 'sports_hernia', name: 'Sports Hernia', fullName: 'Sports Hernia / Athletic Pubalgia',
    bodyRegions: ['Abdomen / Groin'], view: 'front', viewBox: VB.abdomen,
    layer: 'muscle', tags: ['groin', 'abdomen', 'sports', 'muscle'], category: 'Abdomen & Pelvis' },

  { id: 'pelvic_floor', name: 'Pelvic Floor Dysfunction', fullName: 'Pelvic Floor Dysfunction',
    bodyRegions: ['Abdomen / Groin'], view: 'front', viewBox: VB.abdomen,
    layer: 'muscle', tags: ['pelvis', 'pelvic floor', 'core'], category: 'Abdomen & Pelvis' },

  { id: 'diastasis_recti', name: 'Diastasis Recti', fullName: 'Diastasis Recti (Abdominal Separation)',
    bodyRegions: ['Abdomen / Groin'], view: 'front', viewBox: VB.abdomen,
    layer: 'muscle', tags: ['abdomen', 'postpartum', 'core'], category: 'Abdomen & Pelvis' },

  //  HEAD / NEUROLOGICAL 
  { id: 'concussion', name: 'Concussion', fullName: 'Concussion / Mild Traumatic Brain Injury (mTBI)',
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.head,
    layer: 'nerve', tags: ['head', 'concussion', 'brain', 'sports'], category: 'Neurological' },

  { id: 'vestibular_disorder', name: 'Vestibular Disorder', fullName: 'Vestibular Disorder / BPPV',
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.head,
    layer: 'nerve', tags: ['head', 'dizziness', 'balance', 'vestibular'], category: 'Neurological' },

  { id: 'stroke_rehab', name: 'Stroke Rehabilitation', fullName: 'Stroke Rehabilitation (Hemiplegia / Hemiparesis)',
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.head,
    layer: 'nerve', tags: ['stroke', 'neurological', 'rehab'], category: 'Neurological' },

  { id: 'parkinsons', name: "Parkinson's Disease", fullName: "Parkinson's Disease Rehabilitation",
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.head,
    layer: 'nerve', tags: ['parkinsons', 'neurological', 'movement'], category: 'Neurological' },

  { id: 'multiple_sclerosis', name: 'Multiple Sclerosis', fullName: 'Multiple Sclerosis (MS) Rehabilitation',
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.full,
    layer: 'nerve', tags: ['ms', 'neurological', 'fatigue'], category: 'Neurological' },

  { id: 'brachial_plexus', name: 'Brachial Plexus Injury', fullName: 'Brachial Plexus Injury',
    bodyRegions: ['Head / Neck', 'Left Shoulder', 'Right Shoulder'], view: 'front', viewBox: VB.bothShoulders,
    layer: 'nerve', tags: ['nerve', 'shoulder', 'arm', 'brachial plexus'], category: 'Neurological' },

  { id: 'peripheral_neuropathy', name: 'Peripheral Neuropathy', fullName: 'Peripheral Neuropathy',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'front', viewBox: VB.full,
    layer: 'nerve', tags: ['nerve', 'feet', 'hands', 'neuropathy', 'diabetes'], category: 'Neurological' },

  { id: 'crps', name: 'Complex Regional Pain', fullName: 'Complex Regional Pain Syndrome (CRPS)',
    bodyRegions: ['Left Wrist / Hand', 'Right Wrist / Hand'], view: 'front', viewBox: VB.leftWrist,
    layer: 'nerve', tags: ['nerve', 'pain', 'crps', 'chronic'], category: 'Neurological' },

  { id: 'tmj', name: 'TMJ Disorder', fullName: 'Temporomandibular Joint (TMJ) Disorder',
    bodyRegions: ['Head / Neck'], view: 'front', viewBox: VB.head,
    layer: 'joint', tags: ['jaw', 'tmj', 'head', 'joint'], category: 'Neurological' },

  //  SYSTEMIC / GENERAL 
  { id: 'fibromyalgia', name: 'Fibromyalgia', fullName: 'Fibromyalgia',
    bodyRegions: ['Upper Back', 'Lower Back', 'Head / Neck'], view: 'back', viewBox: VB.full,
    layer: 'muscle', tags: ['fibromyalgia', 'chronic pain', 'systemic'], category: 'Systemic' },

  { id: 'osteoporosis', name: 'Osteoporosis', fullName: 'Osteoporosis / Osteopenia',
    bodyRegions: ['Upper Back', 'Lower Back'], view: 'back', viewBox: VB.midBack,
    layer: 'bone', tags: ['bone', 'osteoporosis', 'fracture risk'], category: 'Systemic' },

  { id: 'post_covid', name: 'Post-COVID Rehabilitation', fullName: 'Post-COVID Syndrome Rehabilitation',
    bodyRegions: ['Chest / Ribs'], view: 'front', viewBox: VB.chest,
    layer: 'muscle', tags: ['covid', 'fatigue', 'deconditioning'], category: 'Systemic' },

  { id: 'fall_prevention', name: 'Fall Prevention', fullName: 'Fall Prevention & Balance Training',
    bodyRegions: ['Left Ankle / Foot', 'Right Ankle / Foot'], view: 'front', viewBox: VB.full,
    layer: 'nerve', tags: ['balance', 'fall prevention', 'elderly', 'gait'], category: 'Systemic' },

  { id: 'lymphedema', name: 'Lymphedema', fullName: 'Lymphedema',
    bodyRegions: ['Left Wrist / Hand', 'Right Wrist / Hand', 'Left Ankle / Foot', 'Right Ankle / Foot'],
    view: 'front', viewBox: VB.full,
    layer: 'muscle', tags: ['lymphedema', 'swelling', 'cancer', 'post-surgical'], category: 'Systemic' },

  { id: 'post_fracture', name: 'Post-Fracture Rehab', fullName: 'Post-Fracture Rehabilitation (General)',
    bodyRegions: ['Upper Back', 'Lower Back'], view: 'front', viewBox: VB.full,
    layer: 'bone', tags: ['fracture', 'bone', 'recovery', 'cast'], category: 'Systemic' },

  { id: 'spinal_cord_injury', name: 'Spinal Cord Injury', fullName: 'Spinal Cord Injury Rehabilitation',
    bodyRegions: ['Upper Back', 'Mid Back', 'Lower Back'], view: 'back', viewBox: VB.midBack,
    layer: 'nerve', tags: ['spinal cord', 'paralysis', 'neurological', 'trauma'], category: 'Systemic' },
]

export const INJURY_CATEGORIES = [...new Set(INJURIES.map(i => i.category))]

export default INJURIES
